import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Style from './Governance.module.css';
import LoaderWrapper from '../common/LoaderWrapper/LoaderWrapper';
import PolicyVersionBanner from './PolicyVersionBanner';
import GovernanceLifecycleModals from './GovernanceLifecycleModals';
import useGovernanceLifecycle from './useGovernanceLifecycle';
import { DISPOSITION_BADGE, policyIdFromSearch, editorPath } from './GovernanceShared';
import {
  listExtensionPolicies, listExtensionPredefinedRules, attachExtensionRule, updateExtensionRule,
  createExtensionDraft, clearExtensionRuleError,
  approveGovernancePolicy, rejectGovernancePolicy, publishGovernancePolicy,
} from '../../store/entities/governance';

// ─────────────────────────────────────────────────────────────────────────────
// ExtensionControlsEditor — one screen for both attaching and editing an extension
// rule, reached from the Extension Controls landing page.
//
//   • ATTACH (no ?policyId=) — the rule is a picker and the banner runs in
//     allowInitialSave mode, so its primary action is Save Draft. Saving attaches the
//     rule and the SAME screen becomes that policy's editor, so Submit for Approval
//     and Publish follow without leaving the page.
//   • EDIT   (?policyId=)    — the extension is fixed; the VERDICT, reason and enabled
//     are staged and saved.
//
// The attach → edit handover is driven by the policyVersionId the attach returns (see
// createdVersionId below), NOT by the URL: policyId is a newer field in that response
// and is absent on older API builds. The URL is updated too when it is available, but
// only as a nicety.
//
// ONE COMPONENT, ONE LAYOUT, deliberately: the header, the PolicyVersionBanner and
// the rule card are identical in both modes, so attaching and editing look and behave
// the same and the draft → approve → publish lifecycle is present from the first
// screen. (Web/AI split these into a separate creator with no banner; extensions do
// not, because an extension policy is a single rule — there is no multi-step setup to
// get through before the lifecycle becomes meaningful.)
//
// WHAT IS EDITABLE IS DELIBERATELY NARROW. Rule DEFINITIONS are seeded, read-only
// MST_SCAN_RULE rows and the EXTENSION a policy governs is fixed — retargeting is a
// remove plus a new attach. What can change is the verdict, the reason and the enabled
// flag.
//
// THE VERDICT SWITCH (Denied ⇄ Sanctioned) is a rule swap WITHIN the pair the catalogue
// pairs to this one extension: its SR_EXT_DENY_* rule and its SR_EXT_ALLOW_* rule. Both
// name this extension and only this one, so the picker below offers exactly those two and
// the backend re-checks the pairing.
//
// EDITING A PUBLISHED POLICY IS ALLOWED, and is how a live verdict is changed. Fields stay
// editable while PUBLISHED; the draft is not opened until Save Draft, at which point
// ensureDraft() clones the published version into VersionNumber + 1 and the edits land
// there. That is the same "stage against published, branch on save" flow SiteControlsEditor
// uses for Web/AI, and it is what makes every such change carry a version bump.
//
// NO PER-EXTENSION GET IS NEEDED. listExtensionPolicies already returns every policy
// with its published AND open version state plus the attached rule, so this selects
// its own row from that list rather than requiring a new endpoint.
// ─────────────────────────────────────────────────────────────────────────────

// A predefined rule's action implies the disposition it will carry once published.
const DISPOSITION_FOR_ACTION = { BLOCK: 'DENIED', IGNORE: 'SANCTIONED', REPORT: 'UNSANCTIONED' };

// The verdicts a per-extension rule may carry, and the only two the API accepts. REPORT is absent
// deliberately: it belongs to the SR_EXT_ANY catch-all, whose action the database forces, and
// offering it here would attach something that looks governed but enforces nothing.
const ACTION_DENIED = 'BLOCK';
const VERDICT_OPTIONS = [
  { value: ACTION_DENIED, label: 'Deny — disable it wherever it is found' },
  { value: 'IGNORE', label: 'Sanction — approve it and leave it in place' },
];

const BANNER_LABELS = {
  newPolicy: 'Choose a rule, then click Save Draft to attach it and create the draft.',
  editing: 'Editing the published policy — change the verdict, reason or enabled state to start a new draft.',
  rejected: 'Rejected — revise the rule and resubmit for approval.',
};

const ExtensionEditorBase = (props) => {
  const { customerId, policyId, policies, predefinedRules, publishing, history } = props;

  const [selectedRuleId, setSelectedRuleId] = useState('');
  const [reason, setReason] = useState('');
  const [active, setActive] = useState(true);
  // The verdict being staged in EDIT mode: which of the extension's two paired rules
  // should govern it. Separate from selectedRuleId, which is the attach-mode picker over
  // the whole catalogue.
  // The staged VERDICT: BLOCK (Denied) or IGNORE (Sanctioned). It used to be the id of a second
  // seeded rule -- every extension had a Deny rule and an Allow rule identical in all else -- and
  // is now an action on the one rule that names the extension.
  const [verdict, setVerdict] = useState('');
  const [seed, setSeed] = useState({ reason: '', active: true, verdict: '' });
  // The version id returned by a successful attach. This — NOT the URL — is what
  // flips the screen from attach to edit, because the attach response is only
  // guaranteed to carry policyVersionId; policyId is a newer addition and is absent
  // on older API builds. Keying the transition off the URL meant that against such a
  // build the screen silently stayed in attach mode: the banner kept offering only
  // Save Draft (never Approve/Publish), and the refetched list dropped the now-
  // attached rule out of the picker, blanking every field.
  const [createdVersionId, setCreatedVersionId] = useState(null);

  const createMode = !policyId && !createdVersionId;

  const policy = useMemo(() => {
    const rows = Array.isArray(policies) ? policies : [];
    if (policyId) return rows.find((p) => p.policyId === policyId) || null;
    // Matched on the version we just created — an exact identity, no guessing.
    if (createdVersionId) return rows.find((p) => p.openVersionId === createdVersionId) || null;
    return null;
  }, [policies, policyId, createdVersionId]);
  // THE PENDING RULE WINS HERE, and only here. `policy.rule` is read from the PUBLISHED version
  // whenever there is one (deliberately -- the policy table reports what is live and enforced),
  // so seeding this editor from it showed an admin the OLD values immediately after they saved a
  // draft, and again after approving it. The edit only appeared once publishing moved
  // CurrentPublishedVersionId onto the new version.
  //
  // draftRule is the open DRAFT/APPROVED version's rule, same shape, null when nothing is open.
  const attachedRule = policy && (policy.draftRule || policy.rule);

  // Only rules not already attached can be attached — the same extension cannot hold
  // two policies, and the backend rejects a duplicate with a 409 anyway.
  const attachedRuleIds = new Set(
    (Array.isArray(policies) ? policies : []).filter((p) => p.rule).map((p) => p.rule.scanRuleId),
  );
  const attachableRules = (Array.isArray(predefinedRules) ? predefinedRules : [])
    .filter((r) => !attachedRuleIds.has(r.scanRuleId));
  const pickedRule = attachableRules.find((r) => String(r.scanRuleId) === String(selectedRuleId)) || null;

  // Settable on anything but the catch-all, in BOTH modes.
  //
  // In ATTACH mode it has to be asked for: the verdict is no longer implied by which rule you
  // pick, so without this an attach would silently default to Deny and an admin wanting to
  // sanction something would have to attach it as Denied and then edit it.
  //
  // The catch-all reports and is not a verdict -- the proc forces its action to REPORT -- so it
  // renders as a badge, not a picker.
  const verdictTargetRule = createMode ? pickedRule : attachedRule;
  const canSetVerdict = !!(verdictTargetRule
    && !verdictTargetRule.isDefaultRule
    && (createMode || verdictTargetRule.logicalExtensionId));

  // The rule being shown: the picked one while attaching, the attached one when editing.
  const rule = createMode ? pickedRule : attachedRule;
  const isDefault = !!(rule && rule.isDefaultRule);

  const draftExists = !createMode && !!(policy && policy.openVersionId);
  const status = createMode
    ? 'DRAFT'
    : (policy && policy.openStatus) || (policy && policy.publishedVersionId ? 'PUBLISHED' : 'DRAFT');
  const versionNumber = createMode || !policy
    ? null
    : (policy.openVersionNumber != null ? policy.openVersionNumber : policy.publishedVersionNumber);

  // Editable while DRAFT or PUBLISHED, never while APPROVED.
  //
  // PUBLISHED is editable here but NOT on the server (every extension write is DRAFT-only,
  // ERR_GOV_005). That is not a contradiction: the edits are staged locally and flush()
  // opens the draft before sending anything, so the server only ever sees a write against
  // a DRAFT. Without this a live rule could not be disabled and a live verdict could not be
  // changed -- the fields would simply be greyed out with no way forward.
  //
  // APPROVED stays read-only in both places: editing it would silently invalidate the
  // approval it already carries.
  const isEditable = createMode || status === 'DRAFT' || status === 'PUBLISHED';
  const dirty = createMode
    ? !!selectedRuleId
    : (reason !== seed.reason || active !== seed.active
       || verdict !== seed.verdict);

  // Shaped for the hook's publishedText(), which reads policy.version.versionNumber.
  const publishedShape = useMemo(
    () => (!createMode && policy && policy.publishedVersionId
      ? { version: { versionNumber: policy.publishedVersionNumber } }
      : null),
    [createMode, policy],
  );

  // The hook fires getGovernanceDraft AND getGovernancePolicy together on mount and
  // after every save/approve/reject. For extensions BOTH are the same list read — it
  // already carries draft and published state — so only one is wired up and the other
  // resolves immediately. Pointing both at the list issued two identical GETs per
  // action.
  const refetch = () => props.listExtensionPolicies(customerId);
  const noopFetch = () => Promise.resolve();

  // The predefined catalogue drives BOTH pickers: the attach-mode rule list and the
  // edit-mode verdict switch (which needs the OTHER half of this extension's rule pair),
  // so it is fetched in both modes rather than only while attaching.
  useEffect(() => {
    if (customerId) props.listExtensionPredefinedRules(customerId);
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show the SERVER's reason for a failed attach/edit rather than the lifecycle
  // hook's generic "some changes could not be saved" — the hook only sees that flush()
  // returned null, so without this the admin never learns it was, say, ERR_GOV_019
  // ("an extension policy for this extension already exists"), which tells them
  // exactly what to do next.
  useEffect(() => {
    if (!props.extensionRuleErrorDesc) return;
    lc.setErrorPopup(props.extensionRuleErrorDesc);
    props.clearExtensionRuleError();
  }, [props.extensionRuleErrorDesc]); // eslint-disable-line react-hooks/exhaustive-deps

  // CONTRACT: flush() returns the POLICY VERSION ID to act on, or null if the changes
  // could not be saved — the hook feeds the return value straight into
  // approve/publish. Returning the API response (or undefined when nothing is dirty)
  // makes Save/Approve fail with "the draft could not be saved" even on success.
  const flush = async () => {
    if (createMode) {
      if (!pickedRule) return null;
      const body = {
        scanRuleId: pickedRule.scanRuleId,
        decisionReason: reason || undefined,
        // The catch-all takes no verdict; the proc forces REPORT and rejects one supplied here.
        actionCode: pickedRule.isDefaultRule ? undefined : (verdict || ACTION_DENIED),
      };
      // The default rule targets every extension and must NOT name one — the backend
      // rejects a logicalExtensionId alongside it.
      if (!pickedRule.isDefaultRule) body.logicalExtensionId = pickedRule.logicalExtensionId;

      const res = await props.attachExtensionRule(customerId, body);
      // On any HTTP error res is undefined (the api middleware's portal-wide convention).
      if (res === undefined) return null;
      const created = (res.data && res.data[0]) || {};
      const vid = created.policyVersionId || null;
      if (!vid) return null;

      // THIS is what flips the screen into edit mode — not the URL. policyVersionId is
      // the one field every API build returns, so the handover cannot depend on
      // policyId being present.
      setCreatedVersionId(vid);

      // Best-effort: give the page a real, reloadable URL when the API is new enough
      // to return policyId. Purely cosmetic — the transition above already happened —
      // and replace, not push, so Back does not return to an attach form for a policy
      // that now exists.
      if (created.policyId) history.replace(editorPath(customerId, 'EXTENSION', created.policyId));
      return vid;
    }

    const targetPolicyId = policyId || (policy && policy.policyId);
    if (!targetPolicyId) return null;

    // Nothing staged and a draft already open: it is already what should be approved.
    if (!dirty && policy && policy.openVersionId) return policy.openVersionId;

    // ONE call covers both cases. POST /draft { policyId } resumes an open DRAFT and clones
    // the published version when there is none, so the editor does not have to decide which
    // it is -- and, critically, it returns the RULE ID ON THAT VERSION.
    //
    // That return value is why this is not optional even when a draft is already open: the
    // policy LIST reads the attached rule from the PUBLISHED version whenever one exists
    // (deliberately -- the landing page shows what is live), so attachedRule.ruleId can
    // belong to the published version while a draft is open. PUT-ing that id would come
    // back ERR_GOV_005 on a draft the admin had just opened.
    const res = await props.createExtensionDraft(customerId, targetPolicyId);
    // On any HTTP error res is undefined (the api middleware's portal-wide convention).
    // ERR_GOV_007 lands here when an APPROVED version is blocking, and the server's own
    // message is surfaced by the extensionRuleErrorDesc effect above.
    if (res === undefined) return null;
    const draft = (res.data && res.data[0]) || {};
    const vid = draft.policyVersionId || null;
    if (!vid) return null;

    if (!dirty) return vid;

    const ruleId = draft.ruleId || (attachedRule && attachedRule.ruleId) || null;
    if (!ruleId) return null;

    const body = { decisionReason: reason || null, isActive: active };
    // Only sent when it actually changed. Sending the current id back is harmless but
    // pointless, and omitting it keeps a reason-only edit a reason-only edit.
    if (verdict && verdict !== seed.verdict) {
      body.actionCode = verdict;
    }
    const upd = await props.updateExtensionRule(customerId, ruleId, body);
    return upd === undefined ? null : vid;
  };

  const lc = useGovernanceLifecycle({
    customerId, controlType: 'EXTENSION', history,
    policy: publishedShape, publishing,
    policyVersionId: policy && policy.openVersionId,
    flush, dirty, allowInitialSave: createMode, saveErrorNoun: 'extension rule',
    getGovernanceDraft: refetch,
    getGovernancePolicy: noopFetch,
    approveGovernancePolicy: props.approveGovernancePolicy,
    rejectGovernancePolicy: props.rejectGovernancePolicy,
    publishGovernancePolicy: props.publishGovernancePolicy,
  });

  // Re-seed from the server after every load and after each in-place refresh
  // (Save Draft / Submit / Reject bump reseedKey). The seed is the dirty baseline, so
  // it must always be the true server value. Saving also mints a NEW ruleId (the proc
  // replaces the version's rule), which is the other reason this re-reads rather than
  // trusting its local copy. Skipped while attaching — there is no server row yet.
  useEffect(() => {
    if (createMode || !lc.ready || !attachedRule) return;
    const next = {
      reason: attachedRule.decisionReason || '',
      active: attachedRule.isActive !== false,
      verdict: attachedRule.actionCode || '',
    };
    setSeed(next);
    setReason(next.reason);
    setActive(next.active);
    setVerdict(next.verdict);
  }, [createMode, lc.ready, lc.reseedKey, attachedRule]); // eslint-disable-line react-hooks/exhaustive-deps

  // In edit mode the badge previews the STAGED verdict, not the saved one -- switching
  // Denied -> Sanctioned should show Sanctioned immediately, otherwise the admin cannot
  // tell the change registered until after publish.
  const dispositionCode = createMode
    ? (pickedRule && DISPOSITION_FOR_ACTION[
      pickedRule.isDefaultRule ? 'REPORT' : (verdict || ACTION_DENIED)
    ])
    : (verdict
      ? DISPOSITION_FOR_ACTION[verdict]
      : (attachedRule && attachedRule.dispositionCode));
  const dispositionMeta = dispositionCode && DISPOSITION_BADGE[dispositionCode];

  const extensionLabel = () => {
    if (!rule) return <span className={Style.dimCell}>—</span>;
    if (isDefault) return 'Any other extension';
    return rule.extensionName || rule.logicalExtensionId || '—';
  };

  const title = createMode
    ? 'Attach a Predefined Rule'
    : (isDefault
      ? 'Any other extension'
      : (attachedRule && (attachedRule.extensionName || attachedRule.logicalExtensionId)) || 'Extension rule');

  return (
    <div className={Style.gvPage}>
      <div className={Style.gvHeader}>
        <h1 className={Style.pageTitle}>{title}</h1>
        <p className={Style.pageSubtitle}>
          {createMode
            ? 'Rule definitions are maintained in the database and cannot be edited. Choose one to govern an extension, then use the version banner below to save the draft, submit it for approval, and publish.'
            : 'The rule and the extension it governs are fixed. Edits are staged locally — use the version banner below to save the draft, submit it for approval, or publish.'}
        </p>
        <p className={Style.publishedSubtitle}>{lc.publishedText()}</p>
      </div>

      <div className={Style.gvScrollBody}>
        <PolicyVersionBanner
          statusCode={status}
          versionNumber={versionNumber}
          draftExists={draftExists}
          dirty={dirty}
          allowInitialSave={createMode}
          onSaveDraft={lc.handleSaveDraft}
          onSubmitForApproval={() => lc.setPopup('approve')}
          onReject={() => lc.setPopup('reject')}
          onPublish={() => lc.setPopup('publish')}
          onCancel={lc.handleCancelClick}
          saving={lc.savingDraft}
          publishing={lc.publishing}
          disabled={lc.isBusy}
          labels={BANNER_LABELS}
        />

        <LoaderWrapper loading={!lc.ready} waitForData={false} styles={{ minHeight: '40px' }} isFlex={true}>
          <div className={Style.editorStack}>
            {/* `!lc.isBusy` matters during the attach → edit handover: the URL already
                carries the new policyId while the refetched list is still in flight,
                so without it this would flash "could not be found" on every attach. */}
            {!createMode && !policy && !lc.isBusy ? (
              <div className={Style.card}>
                <div className={Style.cardBody}>
                  <p className={Style.cardSubtitle}>
                    This extension policy could not be found. It may have been removed — go back to
                    Extension Controls and try again.
                  </p>
                </div>
              </div>
            ) : (
              <div className={Style.card}>
                <div className={Style.cardHeader}>
                  <div className={Style.cardTitle}>Extension Rule</div>
                  <div className={Style.cardSubtitle}>
                    {createMode
                      ? 'Choose the extension, then the verdict. Denied extensions are disabled on every managed browser where they are found; sanctioned ones are approved and left in place.'
                      : 'The rule and the extension it governs are fixed; the verdict is not. Switching to Sanctioned re-enables the extension wherever PIXM had disabled it, once the new version is published.'}
                  </div>
                </div>
                <div className={Style.cardBody}>
                  <table className={Style.table}>
                    <tbody>
                      <tr>
                        <th style={{ width: '220px' }}>Extension</th>
                        <td>{extensionLabel()}</td>
                      </tr>
                      <tr>
                        <th><label htmlFor="ext-editor-rule">Rule</label></th>
                        <td>
                          {createMode ? (
                            <>
                              <select
                                id="ext-editor-rule"
                                className={Style.sortSelect}
                                value={selectedRuleId}
                                onChange={(e) => setSelectedRuleId(e.target.value)}
                              >
                                <option value="">Select a rule…</option>
                                {attachableRules.map((r) => (
                                  <option key={r.scanRuleId} value={r.scanRuleId}>
                                    {r.isDefaultRule
                                      ? `${r.scanRuleName} (applies to every extension)`
                                      : `${r.scanRuleName} — ${r.extensionName || r.logicalExtensionId}`}
                                  </option>
                                ))}
                              </select>
                              {attachableRules.length === 0 && (
                                <p className={Style.statNote}>Every predefined rule is already attached.</p>
                              )}
                            </>
                          ) : (                            (attachedRule && attachedRule.scanRuleName) || <span className={Style.dimCell}>—</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th><label htmlFor="ext-editor-policy">Policy</label></th>
                        <td>
                          {/* THE VERDICT lives here, not on the Rule row. It used to be a rule
                              picker there, because Denied and Sanctioned were two different seeded
                              rules; now it is an action on the one rule that names the extension,
                              so it belongs beside the disposition it sets. */}
                          {canSetVerdict ? (
                            <>
                              <select
                                id="ext-editor-policy"
                                className={Style.sortSelect}
                                value={verdict || ACTION_DENIED}
                                disabled={!isEditable}
                                onChange={(e) => setVerdict(e.target.value)}
                              >
                                {VERDICT_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                              {/* The badge stays beside the picker rather than being replaced by
                                  it: the select says what you are choosing, the badge says what
                                  the extension's disposition becomes. Dropping it would remove
                                  the at-a-glance answer from the only row that carries it. */}
                              {dispositionMeta && (
                                <span
                                  className={`${Style.badge} ${Style[dispositionMeta.cls]}`}
                                  style={{ marginLeft: '8px' }}
                                >
                                  {dispositionMeta.label}
                                </span>
                              )}
                              {!createMode && status === 'PUBLISHED' && verdict !== seed.verdict && (
                                <p className={Style.statNote}>
                                  Saving this creates a new version of the published policy. It
                                  takes effect once the new version is approved and published.
                                </p>
                              )}
                            </>
                          ) : dispositionMeta ? (
                            <span className={`${Style.badge} ${Style[dispositionMeta.cls]}`}>{dispositionMeta.label}</span>
                          ) : (
                            <span className={Style.dimCell}>—</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th><label htmlFor="ext-editor-reason">Reason</label></th>
                        <td>
                          <input
                            id="ext-editor-reason"
                            className={Style.input}
                            value={reason}
                            maxLength={1000}
                            disabled={!isEditable}
                            placeholder="Why this rule is applied"
                            onChange={(e) => setReason(e.target.value)}
                          />
                        </td>
                      </tr>
                      <tr>
                        <th><label htmlFor="ext-editor-active">Enabled</label></th>
                        <td>
                          <input
                            id="ext-editor-active"
                            type="checkbox"
                            checked={active}
                            // A rule is always attached enabled; disabling is an edit
                            // made afterwards, so this stays read-only while attaching.
                            // In edit mode it is live even on a PUBLISHED policy -- that
                            // is what "disable a published rule" needs, and Save Draft
                            // branches a new version to carry it.
                            disabled={!isEditable || createMode}
                            onChange={(e) => setActive(e.target.checked)}
                            style={{ cursor: isEditable && !createMode ? 'pointer' : 'default' }}
                          />
                          {isDefault && !active && (
                            <span className={Style.statNote} style={{ marginLeft: '8px' }}>
                              Disabling the default rule stops all extension reporting for this
                              customer — only a PixmAdmin can do it.
                            </span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {!isEditable && (
                    <p className={Style.statNote}>
                      This version is {String(status).toLowerCase()} and is read-only. Approve or
                      reject it before making further changes.
                    </p>
                  )}
                  {!createMode && isEditable && status === 'PUBLISHED' && (
                    <p className={Style.statNote}>
                      This policy is live. Any change here starts a new version, which has to be
                      approved and published before managed browsers pick it up.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </LoaderWrapper>
      </div>

      <GovernanceLifecycleModals
        popup={lc.popup} setPopup={lc.setPopup}
        rejectReason={lc.rejectReason} setRejectReason={lc.setRejectReason}
        errorPopup={lc.errorPopup} setErrorPopup={lc.setErrorPopup}
        isBusy={lc.isBusy} publishing={lc.publishing}
        onCancelConfirmed={lc.handleCancelConfirmed}
        onApproveConfirmed={lc.handleApproveConfirmed}
        onRejectConfirmed={lc.handleRejectConfirmed}
        onPublishConfirmed={lc.handlePublishConfirmed}
        rejectPlaceholder="Why is this extension rule being rejected?"
        publishDescription="This publishes the extension policy. It takes effect on managed browsers at their next heartbeat."
      />
    </div>
  );
};

const ExtensionEditor = connect(
  (state) => ({
    policies: state.entities.governance.extensionPolicies,
    predefinedRules: state.entities.governance.extensionPredefinedRules,
    publishing: state.entities.governance.publishing,
    extensionRuleErrorDesc: state.entities.governance.extensionRuleErrorDesc,
  }),
  (dispatch) => ({
    listExtensionPolicies: (customerId) => Promise.resolve(dispatch(listExtensionPolicies(customerId))),
    listExtensionPredefinedRules: (customerId) => Promise.resolve(dispatch(listExtensionPredefinedRules(customerId))),
    clearExtensionRuleError: () => dispatch(clearExtensionRuleError()),
    attachExtensionRule: (customerId, body) => Promise.resolve(dispatch(attachExtensionRule(customerId, body))),
    createExtensionDraft: (customerId, policyId) => Promise.resolve(dispatch(createExtensionDraft(customerId, policyId))),
    updateExtensionRule: (customerId, ruleId, body) => Promise.resolve(dispatch(updateExtensionRule(customerId, ruleId, body))),
    approveGovernancePolicy: (customerId, versionId, controlType) => Promise.resolve(dispatch(approveGovernancePolicy(customerId, versionId, controlType))),
    rejectGovernancePolicy: (customerId, versionId, reason, controlType) => Promise.resolve(dispatch(rejectGovernancePolicy(customerId, versionId, reason, controlType))),
    publishGovernancePolicy: (customerId, versionId, controlType) => Promise.resolve(dispatch(publishGovernancePolicy(customerId, versionId, controlType))),
  }),
)(ExtensionEditorBase);

const ExtensionControlsEditor = ({ location, match, history }) => {
  // From the ROUTE, not a prop: GovernanceEditor renders this with no props at all,
  // exactly as it does SiteControlsEditor.
  const customerId = match && match.params && match.params.id;
  const policyId = policyIdFromSearch(location && location.search);
  // DELIBERATELY NOT KEYED on policyId. Saving in attach mode history.replace()s to
  // the new policy's URL, and keying here would unmount/remount mid-save: the staged
  // reason would be wiped, and the fresh instance would render before the refetched
  // policies list contained the new row — leaving `policy` null, `draftExists` false
  // and the banner with NO primary action, so the lifecycle could not be continued
  // without going back to the landing page and re-entering via Edit.
  //
  // Staying mounted lets the same instance flip from attach to edit: the seeding
  // effect re-seeds from the server the moment the attached rule appears. Navigating
  // between two different policies always goes via the landing page, which unmounts
  // this anyway, so there is no stale-state path that a key would protect against.
  return <ExtensionEditor customerId={customerId} policyId={policyId} history={history} />;
};

export default withRouter(ExtensionControlsEditor);
